import type { HttpClient } from '../http/HttpClient.ts';
import type { Comment, Watchers } from './CollabApi.ts';
import type { DescriptionVersion } from './DescriptionsApi.ts';
import type { FileCategory, LibraryFile } from './LibraryApi.ts';

export type DocKind = 'folder' | 'page';

/** A folder or page in the Docs tree. */
export interface DocEntry {
  id: string;
  kind: DocKind;
  parentId: string | null;
  title: string;
  rank: string;
  createdBy: string | null;
  updatedAt: string;
  updatedByName?: string | null;
  /** A page's text (Markdown), when one page is read. */
  markdown?: string;
}

/** A search result: a page or a file, with a snippet (matches between \u0002 and \u0003). */
export type DocsHit =
  | { kind: 'page'; id: string; title: string; parentId: string | null; snippet: string }
  | {
      kind: 'file';
      id: string;
      name: string;
      category: FileCategory;
      folderId: string | null;
      filed: boolean;
      snippet: string;
    };

/** A work item a page is linked to by hand. */
export interface LinkedItem {
  id: string;
  number: number;
  title: string;
  type: string;
  statusId: string;
  /** Linked to an epic: the page shows on every item in it. */
  appliesToChildren: boolean;
}

/** Where to put an entry: under `parentId` (null: the top), between two siblings. */
export interface DocPlace {
  parentId: string | null;
  after: string | null;
  before: string | null;
}

/** The Docs tab: the tree of folders and pages, and a page's versions. */
export class DocsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  #base(org: string, key: string): string {
    return `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}/docs`;
  }

  /** The tree: folders and pages, and the files filed in it. */
  list(org: string, key: string): Promise<{ entries: DocEntry[]; files: LibraryFile[] }> {
    return this.#http.get(this.#base(org, key));
  }

  /** Pages and files matching the words in `q` (as you type). */
  search(org: string, key: string, q: string): Promise<{ results: DocsHit[] }> {
    return this.#http.get(`${this.#base(org, key)}/search?q=${encodeURIComponent(q)}`);
  }

  get(org: string, key: string, id: string): Promise<{ entry: DocEntry }> {
    return this.#http.get(`${this.#base(org, key)}/${id}`);
  }

  create(
    org: string,
    key: string,
    input: { kind: DocKind; title: string; parentId: string | null },
  ): Promise<{ entry: DocEntry }> {
    return this.#http.post(this.#base(org, key), input);
  }

  rename(org: string, key: string, id: string, title: string): Promise<{ entry: DocEntry }> {
    return this.#http.patch(`${this.#base(org, key)}/${id}`, { title });
  }

  move(org: string, key: string, id: string, place: DocPlace): Promise<{ entry: DocEntry }> {
    return this.#http.post(`${this.#base(org, key)}/${id}/move`, place);
  }

  /** Deletes a folder or page and everything inside it. */
  delete(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/${id}`);
  }

  versions(org: string, key: string, id: string): Promise<{ versions: DescriptionVersion[] }> {
    return this.#http.get(`${this.#base(org, key)}/${id}/versions`);
  }

  version(org: string, key: string, id: string, versionId: string): Promise<{ markdown: string }> {
    return this.#http.get(`${this.#base(org, key)}/${id}/versions/${versionId}`);
  }

  restore(org: string, key: string, id: string, versionId: string): Promise<void> {
    return this.#http.post(`${this.#base(org, key)}/${id}/versions/${versionId}/restore`);
  }

  comments(
    org: string,
    key: string,
    id: string,
    before: string | null,
  ): Promise<{ comments: Comment[]; earlier: string | null }> {
    return this.#http.get(`${this.#base(org, key)}/${id}/comments`, {
      query: before === null ? {} : { before },
    });
  }

  comment(org: string, key: string, id: string, text: string): Promise<{ comment: Comment }> {
    return this.#http.post(`${this.#base(org, key)}/${id}/comments`, { text });
  }

  links(org: string, key: string, id: string): Promise<{ links: LinkedItem[] }> {
    return this.#http.get(`${this.#base(org, key)}/${id}/links`);
  }

  /** Link an item (or change whether a link to an epic applies to its items). */
  link(
    org: string,
    key: string,
    id: string,
    number: number,
    appliesToChildren: boolean,
  ): Promise<{ links: LinkedItem[] }> {
    return this.#http.put(`${this.#base(org, key)}/${id}/links/${number}`, { appliesToChildren });
  }

  unlink(org: string, key: string, id: string, number: number): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/${id}/links/${number}`);
  }

  watchers(org: string, key: string, id: string): Promise<Watchers> {
    return this.#http.get(`${this.#base(org, key)}/${id}/watchers`);
  }

  setWatching(org: string, key: string, id: string, watching: boolean): Promise<Watchers> {
    return this.#http.put(`${this.#base(org, key)}/${id}/watch`, { watching });
  }
}
